'use client';

import { cn } from '@core/helpers/misc/GeneralUtils';
import { useNavScrollOptional, type DocumentDraftStatus } from '@/contexts/NavScrollContext';

/** Returns text color class for version based on draft status */
function getVersionColor(status: DocumentDraftStatus | null): string {
  if (!status) {
    // Published version - neutral color
    return 'text-gray-500 dark:text-white/60';
  }
  switch (status) {
    case 'new':
      return 'text-muted-foreground'; // gray
    case 'marked_for_review':
      return 'text-violet-500'; // violet
    case 'validated':
      return 'text-brand'; // brand/green
    case 'draft':
    default:
      return 'text-warning'; // warning (yellow/orange)
  }
}

/** Max width for truncated document title (avoid overlapping action bar) */
const TITLE_MAX_WIDTH = 120;

/**
 * NavBreadcrumb - Shows document title + version when scrolled past header
 *
 * Appears in the LEFT zone of the navbar (after org selector) when
 * the document header has scrolled past the navbar (Step 1).
 */
export function NavBreadcrumb() {
  const navScroll = useNavScrollOptional();

  // Show when header is hidden (Step 1 of two-step scroll)
  // Delay appearance to let org name animate out first (300ms)
  const showTitle = navScroll?.headerHidden && navScroll?.documentTitle;
  const title = navScroll?.documentTitle ?? '';

  return (
    <div
      className={cn(
        'nav-breadcrumb hidden md:flex items-center ease-out min-w-0',
        // Delay the appearance by 300ms (after org name finishes animating out)
        showTitle
          ? 'opacity-100 translate-x-0 transition-all duration-300 delay-300'
          : 'opacity-0 -translate-x-4 pointer-events-none w-0 overflow-hidden transition-all duration-300 delay-0'
      )}>
      <div className='flex min-w-0 items-baseline gap-1.5'>
        <span
          className='text-sm font-medium text-foreground truncate'
          style={{ maxWidth: TITLE_MAX_WIDTH }}
          title={title}>
          {title}
        </span>

        {/* Version indicator (draft or commit hash) - colored based on status */}
        {navScroll?.documentVersion && (
          <span className={cn('text-sm shrink-0', getVersionColor(navScroll.documentVersionStatus ?? null))}>
            ({navScroll.documentVersion})
          </span>
        )}
      </div>
    </div>
  );
}

export default NavBreadcrumb;
